import { type ComponentType, useCallback, useEffect, useRef } from "react";
import { match, P } from "ts-pattern";
import { FaEligibilityOutcome } from "@medbillai/generated-graphql-types";
import { FullscreenSpinner, YStack } from "@medbillai/ui";
import { back, createFlow, type FlowState, type FlowTimelineTransitionProps, type FlowTransition, type FlowVisit, type OnDone, type TaggedUnion, type TransitionTable, tagged, useBrowserTimelineSync, useNavigableFlow, visitKeyAt } from "../../lib/flow-engine";
import { BillEducation, type BillEducationOutcomes } from "./BillEducation";
import { FaResultCatastrophic, type FaResultCatastrophicOutcomes } from "./FaResultCatastrophic";
import { FaResultStrongCase, type FaResultStrongCaseOutcomes } from "./FaResultStrongCase";
import { FinancialAssistance, type FinancialAssistanceOutcomes } from "./FinancialAssistance";
import { HouseholdSize, type HouseholdSizeOutcomes } from "./HouseholdSize";
import { Income, type IncomeOutcomes } from "./Income";
import { Insurance, type InsuranceOutcomes } from "./Insurance";
import { type IntentOutcomes, IntentScreen } from "./Intent";
import { Notifications, type NotificationsOutcomes } from "./Notifications";
import { Phone, type PhoneOutcomes } from "./Phone";
import { Profile, type ProfileOutcomes } from "./Profile";
import { SaveEducation, type SaveEducationOutcomes } from "./SaveEducation";
import type { HouseholdData, IdentityData, IntentData, OnboardingIdentity, PartnerData, ResolvedIntentData, RoutingPitchData } from "./types";
import { ValueProps, type ValuePropsOutcomes } from "./ValueProps";
export const ONBOARDING_VERSION = "v3.0" as const;

// `PartnerData` rides the states from every entry `deriveInitial` can pick
// down to the screens that read it: the SaveEducation fork and the strong-case
// co-branding. It is deliberately not ambient context — the derived initial
// state is the single source of the partner fact.
export type OnboardingConfig = {
  ValueProps: {
    needs: {};
    outcomes: ValuePropsOutcomes;
  };
  Profile: {
    needs: PartnerData & ResolvedIntentData & RoutingPitchData;
    outcomes: ProfileOutcomes;
  };
  Phone: {
    needs: {
      identity: OnboardingIdentity;
    } & PartnerData & ResolvedIntentData & RoutingPitchData;
    outcomes: PhoneOutcomes;
  };
  Intent: {
    needs: IdentityData & PartnerData;
    outcomes: IntentOutcomes;
  };
  BillEducation: {
    needs: IntentData & IdentityData;
    outcomes: BillEducationOutcomes;
  };
  SaveEducation: {
    needs: IntentData & IdentityData & PartnerData;
    outcomes: SaveEducationOutcomes;
  };
  FinancialAssistance: {
    needs: IntentData & IdentityData & PartnerData;
    outcomes: FinancialAssistanceOutcomes;
  };
  HouseholdSize: {
    needs: IntentData & IdentityData & PartnerData;
    outcomes: HouseholdSizeOutcomes;
  };
  Income: {
    needs: HouseholdData & IntentData & IdentityData & PartnerData;
    outcomes: IncomeOutcomes;
  };
  FaResultStrongCase: {
    needs: IntentData & IdentityData & PartnerData;
    outcomes: FaResultStrongCaseOutcomes;
  };
  FaResultCatastrophic: {
    needs: IntentData & IdentityData;
    outcomes: FaResultCatastrophicOutcomes;
  };
  Insurance: {
    needs: ResolvedIntentData & IdentityData;
    outcomes: InsuranceOutcomes;
  };
  Notifications: {
    needs: ResolvedIntentData;
    outcomes: NotificationsOutcomes;
  };
  Complete: {
    needs: {};
    outcomes: never;
  };
};
export type OnboardingContext = {
  isWeb: boolean;
};
export const Screen = createFlow<OnboardingConfig>();
const afterPhone = (data: PartnerData & ResolvedIntentData & RoutingPitchData) => match(data).with({
  intent: undefined
}, () => Screen.Intent()).with({
  sawRoutingPitch: false,
  intent: "bill"
}, ({
  intent
}) => Screen.BillEducation({
  intent
})).with({
  sawRoutingPitch: false,
  intent: "save"
}, ({
  intent
}) => Screen.SaveEducation({
  intent
})).with({
  intent: "bill"
}, () => Screen.Insurance()).with({
  intent: "save",
  partner: P.nonNullable
}, ({
  intent
}) => Screen.FinancialAssistance({
  intent
})).with({
  intent: "save",
  partner: null
}, () => Screen.Insurance()).exhaustive();

// Auth-handoff outcomes return the same screen (Noop) so the shell can
// intercept and redirect without breaking the URL-transition animation.
const transitions: TransitionTable<OnboardingConfig, OnboardingContext> = {
  ValueProps: {
    logIn: () => Screen.ValueProps(),
    signUp: () => Screen.ValueProps(),
    back: () => back()
  },
  Profile: {
    submitted: identity => Screen.Phone({
      identity
    })
  },
  Phone: {
    submitted: (_, _ctx, {
      data
    }) => afterPhone(data),
    skipped: (_, _ctx, {
      data
    }) => afterPhone(data)
  },
  Intent: {
    selected: ({
      intent
    }) => intent === "bill" ? Screen.BillEducation({
      intent
    }) : Screen.SaveEducation({
      intent
    }),
    back: () => back()
  },
  BillEducation: {
    continue: () => Screen.Insurance(),
    back: () => back()
  },
  SaveEducation: {
    continue: (_, _ctx, {
      data
    }) => data.partner ? Screen.FinancialAssistance() : Screen.Insurance(),
    back: () => back()
  },
  FinancialAssistance: {
    continue: () => Screen.HouseholdSize(),
    back: () => back()
  },
  HouseholdSize: {
    selected: ({
      householdSize
    }) => Screen.Income({
      householdSize
    }),
    back: () => back()
  },
  Income: {
    checked: ({
      outcome
    }) => match(outcome).with(FaEligibilityOutcome.STRONG_CASE, () => Screen.FaResultStrongCase()).with(FaEligibilityOutcome.CATASTROPHIC_COVERED, () => Screen.FaResultCatastrophic()).exhaustive(),
    back: () => back()
  },
  FaResultStrongCase: {
    continue: () => Screen.Insurance()
  },
  FaResultCatastrophic: {
    continue: () => Screen.Insurance()
  },
  Insurance: {
    // Carrier already persisted on-screen; native shows the push-permission
    // tail, web completes.
    selected: (_, {
      isWeb
    }) => isWeb ? Screen.Complete() : Screen.Notifications(),
    uninsured: (_, {
      isWeb
    }) => isWeb ? Screen.Complete() : Screen.Notifications(),
    skipped: (_, {
      isWeb
    }) => isWeb ? Screen.Complete() : Screen.Notifications()
  },
  Notifications: {
    configured: () => Screen.Complete(),
    skipped: () => Screen.Complete()
  },
  Complete: {}
};
type AuthHandoffShapes = {
  exitToSignup: {};
  exitToLogin: {};
};
export const AuthHandoff = tagged<AuthHandoffShapes>();
export type AuthHandoff = TaggedUnion<AuthHandoffShapes>;
export type OnboardingFlowV3Props = {
  initial: FlowState<OnboardingConfig>;
  ctx: OnboardingContext;
  onComplete: () => void;
  onAuthHandoff: OnDone<AuthHandoffShapes>;
  onTransition?: (event: FlowTransition<OnboardingConfig>) => void;
  /** Fires with the committed state on first render and after every
   * transition — a render cycle after `onTransition`. */
  onStateLoaded?: (state: FlowState<OnboardingConfig>) => void;
  /**
   * Screen-transition wrapper — supplied per-platform by the call site so
   * each platform uses the right animation tech (framer-motion on web,
   * Tamagui+Reanimated on native).
   */
  Transition: ComponentType<FlowTimelineTransitionProps>;
};
export function OnboardingFlowV3({
  initial,
  ctx,
  onAuthHandoff,
  onComplete,
  onTransition: parentOnTransition,
  onStateLoaded,
  Transition
}: OnboardingFlowV3Props) {
  const onTransition = useCallback((event: FlowTransition<OnboardingConfig>) => {
    // Observability sees every outcome, including the auth-handoff ones the
    // match below routes out of the flow.
    parentOnTransition?.(event);
    match([event.from, event.outcome]).with([{
      tag: "ValueProps"
    }, {
      tag: "logIn"
    }], () => {
      onAuthHandoff(AuthHandoff.exitToLogin());
    }).with([{
      tag: "ValueProps"
    }, {
      tag: "signUp"
    }], () => {
      onAuthHandoff(AuthHandoff.exitToSignup());
    }).otherwise(() => {});
  }, [parentOnTransition, onAuthHandoff]);
  const {
    state,
    visits,
    visitKeys,
    activeIndex,
    canGoBack,
    goBack,
    canGoForward,
    goToIndex
  } = useNavigableFlow<OnboardingConfig, OnboardingContext>({
    transitions,
    initial,
    ctx,
    onTransition,
    onStateLoaded
  });

  // Exactly once: running onComplete can legitimately change its own identity
  // (the shell consumes the intended path, recreating the callback), and a
  // re-fire would replace the in-flight redirect with the fallback route.
  const completedRef = useRef(false);
  useEffect(() => {
    if (state.tag === "Complete" && !completedRef.current) {
      completedRef.current = true;
      onComplete();
    }
  }, [state.tag, onComplete]);
  const activeStateKey = visitKeyAt(visitKeys, activeIndex);
  useBrowserTimelineSync({
    visitKeys,
    activeIndex,
    canGoBack,
    goBack,
    canGoForward,
    goToIndex
  });
  const renderScreen = ({
    state: frameState,
    bindOnDone
  }: FlowVisit<OnboardingConfig>) => match(frameState).with({
    tag: "ValueProps"
  }, () => <ValueProps onDone={bindOnDone("ValueProps")} />).with({
    tag: "Profile"
  }, () => <Profile onDone={bindOnDone("Profile")} />).with({
    tag: "Intent"
  }, () => <IntentScreen onDone={bindOnDone("Intent")} />).with({
    tag: "BillEducation"
  }, () => <BillEducation onDone={bindOnDone("BillEducation")} />).with({
    tag: "SaveEducation"
  }, () => <SaveEducation onDone={bindOnDone("SaveEducation")} />).with({
    tag: "FinancialAssistance"
  }, () => <FinancialAssistance onDone={bindOnDone("FinancialAssistance")} />).with({
    tag: "HouseholdSize"
  }, () => <HouseholdSize onDone={bindOnDone("HouseholdSize")} />).with({
    tag: "Income"
  }, ({
    data
  }) => <Income householdSize={data.householdSize} onDone={bindOnDone("Income")} />).with({
    tag: "FaResultStrongCase"
  }, ({
    data
  }) => <FaResultStrongCase onDone={bindOnDone("FaResultStrongCase")} partner={data.partner} />).with({
    tag: "FaResultCatastrophic"
  }, () => <FaResultCatastrophic onDone={bindOnDone("FaResultCatastrophic")} />).with({
    tag: "Insurance"
  }, ({
    data
  }) => <Insurance identity={data.identity} onDone={bindOnDone("Insurance")} />).with({
    tag: "Phone"
  }, ({
    data
  }) => <Phone identity={data.identity} onDone={bindOnDone("Phone")} />).with({
    tag: "Notifications"
  }, ({
    data
  }) => <Notifications intent={data.intent} onDone={bindOnDone("Notifications")} />).with({
    tag: "Complete"
  }, () => <FullscreenSpinner />).exhaustive();
  const frames = visits.map(visit => ({
    stateKey: visit.key,
    content: renderScreen(visit)
  }));
  return (
    // flexBasis auto (web): in the phone page-scroll layout this wrapper
    // must size from its content so the pinned screen's 100dvh floor can
    // grow the page; `flex: 1`'s basis 0 would collapse it. In definite
    // parents (desktop shell, native) grow still fills as before.
    <YStack flex={1} position="relative" overflow="hidden" $platform-web={{
      flexBasis: "auto"
    }} data-sentry-element="YStack" data-sentry-component="OnboardingFlowV3" data-sentry-source-file="OnboardingFlowV3.tsx">
      <Transition frames={frames} activeStateKey={activeStateKey} data-sentry-element="Transition" data-sentry-source-file="OnboardingFlowV3.tsx" />
    </YStack>
  );
}